import { useRef } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalePath } from "@/hooks/useLocalePath";
import { top10Categories, Top10Category, getCategoryUrlSlug } from "@/data/top10Categories";
import { m } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Shirt, Footprints, Leaf, Baby, Users, Waves, Heart, MapPin, Mountain, Tag, Dumbbell, Wind, Briefcase, Snowflake, Palette, Bike, TrendingUp } from "lucide-react";

const slugIcon: Record<string, React.ReactNode> = {
  "made-in-france": <MapPin className="w-6 h-6" />,
  abordable: <Tag className="w-6 h-6" />,
  baskets: <Footprints className="w-6 h-6" />,
  "t-shirts-coton-bio": <Shirt className="w-6 h-6" />,
  "maillots-de-bain": <Waves className="w-6 h-6" />,
  "sous-vetements": <Heart className="w-6 h-6" />,
  "vetements-homme": <Users className="w-6 h-6" />,
  "vetements-bebe-enfant": <Baby className="w-6 h-6" />,
  "vetements-vegan": <Leaf className="w-6 h-6" />,
  "chaussures-vegan": <Footprints className="w-6 h-6" />,
  "outdoor-equipement": <Mountain className="w-6 h-6" />,
  "trail-running": <Dumbbell className="w-6 h-6" />,
  yoga: <Wind className="w-6 h-6" />,
  hiking: <Mountain className="w-6 h-6" />,
  "surf-beachwear": <Waves className="w-6 h-6" />,
  workwear: <Briefcase className="w-6 h-6" />,
  "office-wear": <Briefcase className="w-6 h-6" />,
  freeride: <Snowflake className="w-6 h-6" />,
  "vetements-lin": <Palette className="w-6 h-6" />,
  cycling: <Bike className="w-6 h-6" />,
  climbing: <TrendingUp className="w-6 h-6" />,
};

const Top10Selections = () => {
  const { language } = useLanguage();
  const { getPath } = useLocalePath();
  const isFr = language === "fr";
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 320;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <section className="relative pt-0 pb-16 md:pb-24 bg-background">
      
      <div className="container mx-auto px-6 pt-16 md:pt-24">
        {/* Header – centered */}
        <div className="flex flex-col items-center text-center mb-10">
          <m.span
            className="inline-flex items-center gap-1.5 bg-[#6B8E7A]/15 text-[#6B8E7A] text-xs font-semibold px-3 py-1 rounded-full mb-4"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <Leaf className="w-3 h-3" />
            {isFr ? "Notre sélection" : "Our Selection"}
          </m.span>
          <m.h2
            className="font-serif text-3xl md:text-4xl text-foreground mb-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ textWrap: "pretty" } as React.CSSProperties}
          >
            {isFr ? "Nos sélections" : "Our Selections"}
          </m.h2>
          <p className="text-muted-foreground text-base md:text-lg max-w-xl">
            {isFr
              ? "Découvrez nos classements des marques les plus responsables par catégorie."
              : "Discover our rankings of the most responsible brands by category."}
          </p>
        </div>
        <div className="flex justify-end mb-4">

          {/* Scroll arrows – desktop only */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              className="p-2 rounded-full border border-border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scroll("right")}
              className="p-2 rounded-full border border-border hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable grid */}
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {top10Categories.map((cat) => (
            <SelectionCard key={cat.slug} cat={cat} isFr={isFr} getPath={getPath} />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Link
            to={getPath("/top-10")}
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
          >
            {isFr ? "Voir tous nos classements" : "View all rankings"}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const SelectionCard = ({
  cat,
  isFr,
  getPath,
}: {
  cat: Top10Category;
  isFr: boolean;
  getPath: (p: string) => string;
}) => (
  <m.div
    className="flex-shrink-0 w-[260px] md:w-[280px] snap-start"
    whileHover={{ y: -4 }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
  >
    <Link
      to={getPath(`/top-10/${getCategoryUrlSlug(cat, isFr ? "fr" : "en")}`)}
      className="group flex flex-col h-full rounded-xl border border-border bg-card p-5 transition-colors hover:border-primary/40 hover:shadow-md"
    >
      <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors mb-3">
        {slugIcon[cat.slug] || <Leaf className="w-6 h-6" />}
      </div>
      <h3 className="text-base font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
        {isFr ? cat.labelFr : cat.labelEn}
      </h3>
      <p className="text-sm text-muted-foreground line-clamp-2 flex-1">
        {isFr ? cat.metaDescFr : cat.metaDescEn}
      </p>
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
        {isFr ? "Voir le top 10" : "View top 10"}
        <ArrowRight className="w-3 h-3" />
      </span>
    </Link>
  </m.div>
);

export default Top10Selections;
